import { DialogOptions } from 'components/core/Dialog/common/Dialog';
import { LevelChangeWarnings } from 'shared/domain/documentation/documentationAsMapWarnings';
import { ReactElement } from 'react';
import { FormattedMessage } from 'intl';
import { DialogTranslationKeys, ProjectSetupTranslationKeys } from 'intl/keys';

import { LabelledEntity } from 'shared/types/commonView';
import { DocumentationFile } from 'store/documentation/model/types';

function getReplacementWarningKey(
  level: LabelledEntity,
  index: number
): string {
  return `${level._id || level.label || 'level'}-${index}`;
}

export function showWarningsAsDialog(
  createDialog: (options: DialogOptions) => Promise<void>,
  warnings: LevelChangeWarnings,
  currentFile: DocumentationFile
): Promise<{ warningsConfirmed: boolean }> {
  if (!warnings.replacements.length && !warnings.removals.length) {
    return Promise.resolve({ warningsConfirmed: true });
  }

  return createDialog({
    catchOnCancel: true,
    title: (
      <FormattedMessage id={DialogTranslationKeys.confirmationLevelChangeWarning} />
    ),
    description: (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div>
          <FormattedMessage id={DialogTranslationKeys.confirmationDiscardChangesTitleWeb} />
        </div>
        {warnings.replacements.map((level, index) => (
          <div key={getReplacementWarningKey(level, index)}>
            {toReplacementMessage(currentFile, level)}
          </div>
        ))}
        {warnings.replacements.length ? (
          <div>
            <FormattedMessage id={ProjectSetupTranslationKeys.warningMapReplacementGeneric} />
          </div>
        ) : null}
        {warnings.removals.length ? (
          <RemovalMessage levels={warnings.removals} />
        ) : null}
      </div>
    ),
  })
    .then(() => ({ warningsConfirmed: true }))
    .catch(() => ({ warningsConfirmed: false }));
}

function RemovalMessage({
  levels,
}: {
  levels: LabelledEntity[];
}): ReactElement {
  return (
    <div>
      <FormattedMessage id={ProjectSetupTranslationKeys.warningMapRemoval}
        values={{
          levelString: levels.map((lvl) => lvl.label).join(', '),
          levelsLength: levels.length,
        }}
      />
    </div>
  );
}

function toReplacementMessage(
  currentFile: DocumentationFile,
  level: LabelledEntity
): ReactElement {
  return (
    <div>
      <FormattedMessage id={ProjectSetupTranslationKeys.warningMapReplacementSpecific}
        values={{
          levelLabel: level.label,
          title: currentFile.title,
          extension: currentFile.data?.extension || '',
        }}
      />
    </div>
  );
}
